/**
 * Einstellungen (spec §1.12, Reiter: Stammdaten/Konten, Team & Einladungen, Webhook, Audit-Log,
 * Verfahrensdokumentation).
 *
 * Zwei Dinge sind hier heikel und deshalb ausdrücklich gebaut: `line_customer_format` entscheidet, wie
 * Kunden auf Rechnungen, Belegen und im Steuerberater-Export erscheinen (D5) — es steht bei den
 * Stammdaten, nicht versteckt. Und das Webhook-Secret ist nach dem Erneuern genau einmal sichtbar; der
 * Server speichert nur seinen Hash (services/tenants.ts, `rotateWebhookSecret`), es gibt keinen zweiten
 * Blick.
 *
 * Gerechnet wird hier nichts: der Bildschirm zeigt, was der Server geantwortet hat, und schickt zurück,
 * was der Benutzer geändert hat — vorgeprüft mit denselben zod-Schemata, mit denen der Worker den Körper
 * prüft, damit ein offensichtlicher Tippfehler ohne Umweg über das Netz auffällt. Angezeigt wird die
 * Meldung, die im Schema selbst steht; die deutsche Fehlerkarte (`installGermanErrorMap`) läuft bisher
 * nur im Worker, eine Regel ohne eigenen Text kann hier also englisch melden. Der Server bleibt die
 * Instanz, die entscheidet: seine Antwort wird wörtlich gezeigt.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, type ReactElement, type ReactNode } from 'react'
import { createTeamMemberSchema, inviteTeamMemberSchema } from '../../../../shared/schemas/team'
import { addressSchema, updateTenantSchema } from '../../../../shared/schemas/tenant'
import { api, type AuditZeile, type TeamZeile, type TenantEinstellungen, type WebhookGeheimnis } from '../../../api'
import { Dialog } from '../../../components/Dialog'
import { Feld } from '../../../components/Feld'
import { Knopf } from '../../../components/Knopf'
import { SteuerprofilFreigabeDialog } from '../../../components/SteuerprofilDialog'
import { Leiste } from '../../../components/Leiste'
import { Tabelle } from '../../../components/Tabelle'
import { TabellenZustand, Zustand, fehlermeldung } from '../../../components/Zustand'
import { datumZeit } from '../../../format'
import { DEAL_FELDER, PAYMENT_FELDER, type HlFeld } from '../../../hlFelder'
import { STEUERPROFIL_TEXT } from '../../../rechnungenModell'
import { useSitzung } from '../../../auth'

const REITER = ['stammdaten', 'team', 'webhook', 'audit', 'dokumentation'] as const
export type Reiter = (typeof REITER)[number]

const REITER_TEXT: Readonly<Record<Reiter, string>> = {
  stammdaten: 'Stammdaten & Konten',
  team: 'Team & Einladungen',
  webhook: 'Webhook',
  audit: 'Audit-Log',
  dokumentation: 'Verfahrensdokumentation',
}

export interface EinstellungenSuche {
  tab: Reiter
  entity?: string
  actor?: string
  from?: string
  to?: string
}

/** Die vier Textfelder, deren leere Eingabe „nicht gesetzt“ bedeutet — alle anderen sind Pflicht. */
type NullbaresTextFeld = 'vat_id' | 'tax_number' | 'handelsregister' | 'geschaeftsfuehrer'
type TextFeld =
  | 'name' | 'legal_name' | 'contact_email' | 'billing_email' | 'reply_to_email' | 'receipt_prefix'
  | 'accepts_pdf_invoices_at' | NullbaresTextFeld
type ZahlFeld = 'payment_terms_days' | 'account_expense' | 'account_bank' | 'kreditor_start'
type AdressFeld = 'street' | 'zip' | 'city' | 'country'

/**
 * Zwei Gruppen. Oben, was jeder Auftraggeber kennt: Firma, Adresse, Adressen für Post und Rechnung.
 * Unter „Erweitert“, was nur die Buchhaltung und der Steuerberater brauchen — mit je einem Satz, was es
 * ist. Niemand muss wissen, was ein Aufwandskonto ist, um diese Anwendung zu benutzen.
 */
const TEXT_FELDER: ReadonlyArray<[TextFeld, string]> = [
  ['name', 'Anzeigename'],
  ['legal_name', 'Firmierung'],
  ['vat_id', 'USt-IdNr.'],
  ['tax_number', 'Steuernummer'],
  ['handelsregister', 'Handelsregister'],
  ['geschaeftsfuehrer', 'Geschäftsführung'],
  ['contact_email', 'Kontakt-E-Mail'],
  ['billing_email', 'Rechnungskopie (CC)'],
  ['reply_to_email', 'Antwortadresse'],
]

const ERWEITERT_TEXT_FELDER: ReadonlyArray<[TextFeld, string, string]> = [
  ['receipt_prefix', 'Präfix der Auszahlungsbelege', 'Belegnummern beginnen damit, etwa AB-2026-0001.'],
  ['accepts_pdf_invoices_at', 'PDF-Rechnungen akzeptiert ab', 'Ab diesem Datum gelten Rechnungen aus dieser Anwendung als zugegangen.'],
]

const ERWEITERT_ZAHL_FELDER: ReadonlyArray<[ZahlFeld, string, string]> = [
  ['account_expense', 'Aufwandskonto', 'Das Sachkonto, auf das der Steuerberater Provisionen bucht — im SKR03 meist 4760. Steht im Export.'],
  ['account_bank', 'Bankkonto', 'Das Sachkonto der Bank im Kontenrahmen — im SKR03 meist 1200.'],
  ['kreditor_start', 'Erste Kreditorennummer', 'Jeder Auftragnehmer bekommt bei seiner ersten Freigabe die nächste freie Nummer ab hier; unter ihr führt ihn der Steuerberater.'],
]

const NULLBAR: ReadonlySet<string> = new Set<NullbaresTextFeld>([
  'vat_id', 'tax_number', 'handelsregister', 'geschaeftsfuehrer',
])

const ZAHL_FELDER: ReadonlyArray<[ZahlFeld, string]> = [
  ['payment_terms_days', 'Zahlungsziel in Tagen'],
]

const ADRESS_FELDER: ReadonlyArray<[AdressFeld, string]> = [
  ['street', 'Straße'], ['zip', 'PLZ'], ['city', 'Ort'], ['country', 'Land (zwei Buchstaben)'],
]

const LEERE_ADRESSE: Readonly<Record<AdressFeld, string>> = { street: '', zip: '', city: '', country: '' }

/**
 * `TenantEinstellungen.address` ist am Client `unknown` — der Server gibt das geparste JSON der Spalte
 * `address_json` heraus (services/tenants.ts, `publicTenant`). Geprüft wird es mit demselben Schema,
 * mit dem der Server eine neue Adresse annimmt; ist sie unerwartet geformt, stehen leere Felder da,
 * statt dass der Bildschirm abstürzt.
 */
function adresseVon(tenant: TenantEinstellungen): Record<AdressFeld, string> {
  const geprueft = addressSchema.safeParse(tenant.address)
  return geprueft.success ? geprueft.data : { ...LEERE_ADRESSE }
}

function istReiter(wert: unknown): wert is Reiter {
  return typeof wert === 'string' && (REITER as readonly string[]).includes(wert)
}

function textOderUndefined(wert: unknown): string | undefined {
  return typeof wert === 'string' && wert !== '' ? wert : undefined
}

/**
 * Die erste zod-Meldung, wörtlich, zusammen mit dem Feld, das sie meint — wie in `rechnungen.tsx`.
 * Der Schlüssel ist der zusammengesetzte Pfad (`address.street`), damit auch die Adressfelder ihre
 * eigene Meldung tragen; `feld: null` heißt „gilt dem ganzen Vorgang" (z. B. „Es wurde nichts
 * geändert."). Nur so landet die Meldung an dem Eingabefeld, das sie beanstandet, statt in einem
 * Absatz weit darunter, der für einen Screenreader keinen Bezug zur Eingabe hat.
 */
interface Eingabefehler {
  feld: string | null
  meldung: string
}

interface ZodMeldung { readonly path: readonly PropertyKey[]; readonly message: string }

function ersterFehler(fehler: { issues: readonly ZodMeldung[] }): Eingabefehler {
  const erste = fehler.issues[0]
  if (erste === undefined) return { feld: null, meldung: 'Die Eingabe ist ungültig.' }
  const pfad = erste.path.filter((teil): teil is string => typeof teil === 'string').join('.')
  return { feld: pfad === '' ? null : pfad, meldung: erste.message }
}

/** Die Meldung für genau dieses Feld — oder `undefined`, damit `Feld` es nicht als ungültig markiert. */
function meldungFuer(fehler: Eingabefehler | null, feld: string): string | undefined {
  return fehler !== null && fehler.feld === feld ? fehler.meldung : undefined
}

export const Route = createFileRoute('/t/$tenantId/einstellungen')({
  validateSearch: (roh: Record<string, unknown>): EinstellungenSuche => ({
    tab: istReiter(roh.tab) ? roh.tab : 'stammdaten',
    entity: textOderUndefined(roh.entity),
    actor: textOderUndefined(roh.actor),
    from: textOderUndefined(roh.from),
    to: textOderUndefined(roh.to),
  }),
  component: EinstellungenRoute,
})

function EinstellungenRoute(): ReactNode {
  const { tenantId } = Route.useParams()
  const suche = Route.useSearch()
  const navigate = Route.useNavigate()
  return (
    <EinstellungenScreen
      tenantId={tenantId}
      suche={suche}
      setzeSuche={(naechste) => { void navigate({ to: '.', search: naechste }) }}
    />
  )
}

/**
 * Der Bildschirm bekommt genau drei Dinge: den Auftraggeber, die Suchparameter (Reiter und die vier
 * Filter des Audit-Logs) und den Weg, sie zurückzuschreiben. Ein Sprung auf einen anderen Bildschirm
 * kommt hier nicht vor, deshalb auch kein `gehZu`.
 */
export interface EinstellungenSeitenProps {
  tenantId: string
  suche: EinstellungenSuche
  setzeSuche: (suche: EinstellungenSuche) => void
}

export function EinstellungenScreen({ tenantId, suche, setzeSuche }: EinstellungenSeitenProps): ReactElement {
  const sitzung = useSitzung()
  return (
    <div className="seite">
      <h1>Einstellungen</h1>

      <nav className="jahre" aria-label="Bereiche der Einstellungen">
        {REITER.map((reiter) => (
          <button
            key={reiter}
            type="button"
            aria-current={suche.tab === reiter ? 'true' : undefined}
            onClick={() => { setzeSuche({ ...suche, tab: reiter }) }}
          >
            {REITER_TEXT[reiter]}
          </button>
        ))}
      </nav>

      {suche.tab === 'stammdaten' && <StammdatenReiter tenantId={tenantId} />}
      {suche.tab === 'team' && (
        <TeamReiter tenantId={tenantId} istPlattformAdmin={sitzung.user.is_platform_admin} />
      )}
      {suche.tab === 'webhook' && <WebhookReiter tenantId={tenantId} />}
      {suche.tab === 'audit' && <AuditReiter tenantId={tenantId} suche={suche} setzeSuche={setzeSuche} />}
      {suche.tab === 'dokumentation' && <DokumentationReiter />}
    </div>
  )
}

// -----------------------------------------------------------------------------------------------------
// Stammdaten und Konten
// -----------------------------------------------------------------------------------------------------

function StammdatenReiter({ tenantId }: { tenantId: string }): ReactElement {
  const tid = encodeURIComponent(tenantId)
  const queryClient = useQueryClient()
  const [entwurf, setzeEntwurf] = useState<Record<string, string>>({})
  const [pruefFehler, setzePruefFehler] = useState<Eingabefehler | null>(null)
  const [gespeichert, setzeGespeichert] = useState(false)

  const einstellungen = useQuery({
    queryKey: ['einstellungen', tenantId],
    queryFn: () => api.get<{ tenant: TenantEinstellungen }>(`/api/t/${tid}/einstellungen`),
  })
  const tenant = einstellungen.data?.tenant ?? null

  // Der Entwurf wird aus der Antwort des Servers gesetzt — auch nach dem Speichern, denn dann steht
  // dort, was wirklich gespeichert wurde (getrimmt, großgeschrieben), nicht das Getippte.
  useEffect(() => {
    if (tenant === null) return
    const adresse = adresseVon(tenant)
    const start: Record<string, string> = {}
    for (const [name] of TEXT_FELDER) start[name] = String(tenant[name] ?? '')
    for (const [name] of ZAHL_FELDER) start[name] = String(tenant[name])
    for (const [name] of ADRESS_FELDER) start[`address.${name}`] = adresse[name]
    start.line_customer_format = tenant.line_customer_format
    start.hl_number_format = tenant.hl_number_format
    setzeEntwurf(start)
  }, [tenant])

  const speichern = useMutation({
    mutationFn: (patch: unknown) =>
      api.patch<{ tenant: TenantEinstellungen }>(`/api/t/${tid}/einstellungen`, patch),
    onSuccess: () => {
      setzeGespeichert(true)
      void queryClient.invalidateQueries({ queryKey: ['einstellungen', tenantId] })
    },
  })

  /** Nur das Geänderte geht hinaus: ein PATCH mit allen Feldern schriebe Audit-Zeilen ohne Änderung. */
  const absenden = (): void => {
    if (tenant === null) return
    const alteAdresse = adresseVon(tenant)
    const patch: Record<string, unknown> = {}
    for (const [name] of TEXT_FELDER) {
      const neu = entwurf[name] ?? ''
      if (neu === String(tenant[name] ?? '')) continue
      patch[name] = neu === '' && NULLBAR.has(name) ? null : neu
    }
    // Ein leeres Zahlenfeld ist keine Null. `Number('')` ist 0 und `Number.isFinite(0)` ist wahr —
    // ein geleertes „Zahlungsziel in Tagen" ginge damit als 0 durch das Schema (min(0)) und stünde
    // danach als „zahlbar sofort" auf jeder freigegebenen Rechnung. Ein leeres oder unlesbares Feld
    // ist deshalb ein Eingabefehler an genau diesem Feld, keine stille 0.
    for (const [name] of ZAHL_FELDER) {
      const roh = (entwurf[name] ?? '').trim()
      if (roh === '') {
        speichern.reset()
        setzeGespeichert(false)
        setzePruefFehler({ feld: name, meldung: 'Bitte eine Zahl angeben.' })
        return
      }
      const neu = Number(roh)
      if (!Number.isFinite(neu)) {
        speichern.reset()
        setzeGespeichert(false)
        setzePruefFehler({ feld: name, meldung: 'Bitte eine Zahl angeben.' })
        return
      }
      if (neu === tenant[name]) continue
      patch[name] = neu
    }
    const adresse: Record<AdressFeld, string> = {
      street: entwurf['address.street'] ?? '',
      zip: entwurf['address.zip'] ?? '',
      city: entwurf['address.city'] ?? '',
      country: entwurf['address.country'] ?? '',
    }
    if (ADRESS_FELDER.some(([name]) => adresse[name] !== alteAdresse[name])) patch.address = adresse
    if (entwurf.line_customer_format !== tenant.line_customer_format) {
      patch.line_customer_format = entwurf.line_customer_format
    }
    if (entwurf.hl_number_format !== tenant.hl_number_format) patch.hl_number_format = entwurf.hl_number_format

    const geprueft = updateTenantSchema.safeParse(patch)
    if (!geprueft.success) {
      speichern.reset()
      setzeGespeichert(false)
      setzePruefFehler(ersterFehler(geprueft.error))
      return
    }
    setzePruefFehler(null)
    setzeGespeichert(false)
    speichern.mutate(geprueft.data)
  }

  if (einstellungen.isPending) {
    return <Zustand art="laedt" beschriftung="Die Stammdaten werden geladen." />
  }
  if (tenant === null) {
    return (
      <Zustand
        art="fehler"
        fehler={einstellungen.error}
        onWiederholen={() => { void einstellungen.refetch() }}
      />
    )
  }

  return (
    <section aria-label="Stammdaten und Konten">
      <div className="detail">
        {TEXT_FELDER.map(([name, label]) => (
          <Feld key={name} label={label} htmlFor={`st-${name}`} fehler={meldungFuer(pruefFehler, name)}>
            <input
              id={`st-${name}`}
              type="text"
              value={entwurf[name] ?? ''}
              onChange={(ereignis) => { setzeEntwurf({ ...entwurf, [name]: ereignis.target.value }) }}
            />
          </Feld>
        ))}
        {ADRESS_FELDER.map(([name, label]) => (
          <Feld
            key={name}
            label={label}
            htmlFor={`st-adresse-${name}`}
            fehler={meldungFuer(pruefFehler, `address.${name}`)}
          >
            <input
              id={`st-adresse-${name}`}
              type="text"
              value={entwurf[`address.${name}`] ?? ''}
              onChange={(ereignis) => {
                setzeEntwurf({ ...entwurf, [`address.${name}`]: ereignis.target.value })
              }}
            />
          </Feld>
        ))}
        {ZAHL_FELDER.map(([name, label]) => (
          <Feld key={name} label={label} htmlFor={`st-${name}`} fehler={meldungFuer(pruefFehler, name)}>
            <input
              id={`st-${name}`}
              className="mono"
              type="number"
              value={entwurf[name] ?? ''}
              onChange={(ereignis) => { setzeEntwurf({ ...entwurf, [name]: ereignis.target.value }) }}
            />
          </Feld>
        ))}
      </div>

      <details className="detail">
        <summary>Erweitert — Buchhaltung &amp; Export</summary>
        <p className="hinweis">Was nur der Steuerberater und der Export brauchen. Die Vorgaben passen fast immer.</p>
        {ERWEITERT_TEXT_FELDER.map(([name, label, hinweis]) => (
          <Feld key={name} label={label} htmlFor={`st-${name}`} fehler={meldungFuer(pruefFehler, name)} hinweis={hinweis}>
            <input
              id={`st-${name}`}
              type="text"
              value={entwurf[name] ?? ''}
              onChange={(ereignis) => { setzeEntwurf({ ...entwurf, [name]: ereignis.target.value }) }}
            />
          </Feld>
        ))}
        {ERWEITERT_ZAHL_FELDER.map(([name, label, hinweis]) => (
          <Feld key={name} label={label} htmlFor={`st-${name}`} fehler={meldungFuer(pruefFehler, name)} hinweis={hinweis}>
            <input
              id={`st-${name}`}
              className="mono"
              type="number"
              value={entwurf[name] ?? ''}
              onChange={(ereignis) => { setzeEntwurf({ ...entwurf, [name]: ereignis.target.value }) }}
            />
          </Feld>
        ))}
        <Feld
          label="Kundenangabe auf Belegen"
          htmlFor="st-line-customer-format"
          fehler={meldungFuer(pruefFehler, 'line_customer_format')}
          hinweis="Wie der Kunde auf Rechnungen, Belegen und im Export steht. In der Anwendung selbst immer mit vollem Namen."
        >
          <select
            id="st-line-customer-format"
            value={entwurf.line_customer_format ?? tenant.line_customer_format}
            onChange={(ereignis) => {
              setzeEntwurf({ ...entwurf, line_customer_format: ereignis.target.value })
            }}
          >
            <option value="initials_contact_id">Initialen und Kontakt-ID</option>
            <option value="full_name">Vollständiger Name</option>
          </select>
        </Feld>
        <Feld
          label="Zahlenformat aus FinSuites"
          htmlFor="st-hl-number-format"
          fehler={meldungFuer(pruefFehler, 'hl_number_format')}
          hinweis="Wie FinSuites Beträge schreibt. Nur ändern, wenn Zahlungen als „nicht lesbar“ in der Prüfliste landen."
        >
          <select
            id="st-hl-number-format"
            value={entwurf.hl_number_format ?? tenant.hl_number_format}
            onChange={(ereignis) => { setzeEntwurf({ ...entwurf, hl_number_format: ereignis.target.value }) }}
          >
            <option value="en-US">1,234.56 (en-US)</option>
            <option value="de-DE">1.234,56 (de-DE)</option>
          </select>
        </Feld>
      </details>

      <p className="knoepfe">
        <Knopf disabled={speichern.isPending} onClick={absenden}>Stammdaten speichern</Knopf>
      </p>
      {gespeichert && !speichern.isPending && <p className="meldung">Die Änderungen sind gespeichert.</p>}
      {/* Eine Meldung mit Feld steht bei ihrem Feld; hier bleibt nur, was dem ganzen Formular gilt. */}
      {pruefFehler !== null && pruefFehler.feld === null && (
        <p role="alert" className="fehler">{pruefFehler.meldung}</p>
      )}
      {speichern.isError && <p role="alert" className="fehler">{fehlermeldung(speichern.error)}</p>}
    </section>
  )
}

// -----------------------------------------------------------------------------------------------------
// Team und Einladungen
// -----------------------------------------------------------------------------------------------------

const TEAM_SPALTEN = 8

/** Der Name hinter einer `tenant_contractor_id` — für die Erfolgsmeldung, die sagen soll, WESSEN. */
function zeilenName(zeilen: readonly TeamZeile[], tcId: string): string {
  return zeilen.find((zeile) => zeile.tenant_contractor_id === tcId)?.display_name ?? 'des Auftragnehmers'
}

function TeamReiter({ tenantId, istPlattformAdmin }: {
  tenantId: string
  istPlattformAdmin: boolean
}): ReactElement {
  const tid = encodeURIComponent(tenantId)
  const queryClient = useQueryClient()
  const [einladungen, setzeEinladungen] = useState<Record<string, string>>({})
  /** Entwürfe für „Name in FinSuites" je Zeile; ohne Entwurf zeigt das Feld `legacy_name`. */
  const [hlNamen, setzeHlNamen] = useState<Record<string, string>>({})
  const [neuerName, setzeNeuenNamen] = useState('')
  const [neueEmail, setzeNeueEmail] = useState('')
  const [pruefFehler, setzePruefFehler] = useState<Eingabefehler | null>(null)
  const [meldung, setzeMeldung] = useState<string | null>(null)
  /** Die Zeile, deren Steuerprofil gerade zur Freigabe nachgefragt wird — `null`: kein Dialog. */
  const [freigabe, setzeFreigabe] = useState<TeamZeile | null>(null)
  /** Die Zeile, deren Entfernen gerade nachgefragt wird — `null`: kein Dialog. */
  const [entfernen, setzeEntfernen] = useState<TeamZeile | null>(null)
  const [inaktiveZeigen, setzeInaktiveZeigen] = useState(false)

  const team = useQuery({
    queryKey: ['team', tenantId],
    queryFn: () => api.get<{ team: TeamZeile[] }>(`/api/t/${tid}/team`),
  })
  const neuLaden = (): void => { void queryClient.invalidateQueries({ queryKey: ['team', tenantId] }) }

  const anlegen = useMutation({
    mutationFn: (rumpf: { display_name: string; email?: string }) =>
      api.post<{ mitglied: TeamZeile }>(`/api/t/${tid}/team`, { display_name: rumpf.display_name }),
    onSuccess: (antwort, rumpf) => {
      setzeMeldung(`${antwort.mitglied.display_name} wurde angelegt.`)
      setzeNeuenNamen('')
      neuLaden()
      // Anlegen und einladen sind für den Betreiber ein Schritt: Name, E-Mail, fertig. Die Einladung
      // geht als zweiter Aufruf hinaus; scheitert sie, steht die Person trotzdem im Team und die
      // Zeile bietet das Einladen erneut an.
      const email = (rumpf as { email?: string }).email
      if (email !== undefined && email !== '') {
        einladen.mutate({ tcId: antwort.mitglied.tenant_contractor_id, email })
      } else {
        setzeNeueEmail('')
      }
    },
  })
  const einladen = useMutation({
    mutationFn: (ziel: { tcId: string; email: string }) =>
      api.post<{ einladung_id: string; gesendet_an: string }>(
        `/api/t/${tid}/team/${encodeURIComponent(ziel.tcId)}/invite`, { email: ziel.email }),
    // Das Feld wird geleert: bliebe die Adresse stehen, verschickt der nächste Klick auf denselben
    // Knopf dieselbe Einladung ein zweites Mal.
    onSuccess: (antwort, ziel) => {
      setzeMeldung(`Die Einladung ging an ${antwort.gesendet_an}.`)
      setzeEinladungen((bisher) => ({ ...bisher, [ziel.tcId]: '' }))
      setzeNeueEmail('')
      neuLaden()
    },
  })
  // „Name in FinSuites" ist die Datenbankspalte `legacy_name`: der Name, gegen den die Maschine
  // `closer`, `setter` und `host` aus dem Webhook abgleicht (engine/run.ts, resolveRole). Vor- und
  // Nachname, exakt so, wie FinSuites sie schickt — ein Vorname allein trifft irgendwann zwei
  // Personen, und dann bleibt die Rolle mit Absicht leer.
  const hlName = useMutation({
    mutationFn: (ziel: { tcId: string; name: string | null }) =>
      api.patch<{ mitglied: TeamZeile }>(
        `/api/t/${tid}/team/${encodeURIComponent(ziel.tcId)}`, { legacy_name: ziel.name }),
    onSuccess: (antwort, ziel) => {
      setzeMeldung(
        antwort.mitglied.legacy_name === null
          ? `Name in FinSuites für ${antwort.mitglied.display_name} entfernt.`
          : `Name in FinSuites für ${antwort.mitglied.display_name} gespeichert: ${antwort.mitglied.legacy_name}.`,
      )
      setzeHlNamen((bisher) => {
        const { [ziel.tcId]: _weg, ...rest } = bisher
        return rest
      })
      neuLaden()
    },
  })
  const entferne = useMutation({
    mutationFn: (tcId: string) => api.delete<{ geloescht: boolean }>(`/api/t/${tid}/team/${encodeURIComponent(tcId)}`),
    onSuccess: (antwort, tcId) => {
      const name = zeilenName(team.data?.team ?? [], tcId)
      // Der Server entscheidet, ob die Zeile weg ist oder nur stillgelegt - und die Meldung sagt, was er tat.
      setzeMeldung(antwort.geloescht
        ? `${name} ist entfernt.`
        : `${name} ist stillgelegt: an dieser Person hängen Provisionen, Abschlüsse oder Rechnungen, deshalb bleibt sie im Archiv.`)
      setzeEntfernen(null)
      neuLaden()
    },
  })
  // Nur der Plattform-Administrator darf das Steuerprofil bestätigen (routes/tenant.ts,
  // requirePlatformAdmin) — deshalb steht der Knopf auch nur bei ihm.
  const pruefen = useMutation({
    mutationFn: (tcId: string) =>
      api.post<{ mitglied: TeamZeile }>(`/api/t/${tid}/team/${encodeURIComponent(tcId)}/verify-profile`, {}),
    onSuccess: (_antwort, tcId) => {
      const name = zeilenName(team.data?.team ?? [], tcId)
      setzeMeldung(`Das Steuerprofil von ${name} ist geprüft.`)
      setzeFreigabe(null)
      neuLaden()
    },
  })

  // Nur die Aktionen: ein Ladefehler der Tabelle steht in der Tabelle selbst (TabellenZustand). Käme
  // `team.error` hier zuerst, verschwände die Antwort auf ein fehlgeschlagenes POST — die Formulare
  // unter der Tabelle bleiben ja bedienbar, auch wenn die Liste gerade nicht geladen werden konnte.
  const aktionsFehler = anlegen.error ?? einladen.error ?? pruefen.error ?? hlName.error ?? entferne.error

  const alleZeilen = team.data?.team ?? []
  const zeilen = inaktiveZeigen ? alleZeilen : alleZeilen.filter((zeile) => zeile.active === 1)
  const inaktive = alleZeilen.length - alleZeilen.filter((zeile) => zeile.active === 1).length

  return (
    <section aria-label="Team und Einladungen">
      <Tabelle
        beschriftung="Auftragnehmer dieses Auftraggebers"
        kopf={(
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Kürzel</th>
            <th scope="col">Name in FinSuites</th>
            <th scope="col">Rechnungspräfix</th>
            <th scope="col">Kreditor</th>
            <th scope="col">Steuerprofil</th>
            <th scope="col">Zugang</th>
            <th scope="col"><span className="nur-vorlesen">Entfernen</span></th>
          </tr>
        )}
      >
        {team.isPending ? (
          <TabellenZustand art="laedt" beschriftung="Das Team wird geladen." spalten={TEAM_SPALTEN} />
        ) : team.isError ? (
          <TabellenZustand
            art="fehler"
            fehler={team.error}
            onWiederholen={() => { void team.refetch() }}
            spalten={TEAM_SPALTEN}
          />
        ) : zeilen.length === 0 ? (
          <TabellenZustand
            art="leer"
            titel="Noch niemand im Team"
            hinweis="Legen Sie einen Empfänger an und laden Sie ihn ein. Erst danach kann er abrechnen."
            spalten={TEAM_SPALTEN}
          />
        ) : (
          zeilen.map((zeile) => {
            // Die Meldung der Einladung gehört in DIESE Zeile: ein Absatz unter der Tabelle wäre bei
            // zwanzig Auftragnehmern zwanzig Zeilen entfernt und für einen Screenreader ohne Bezug.
            const einladungsFehler = meldungFuer(pruefFehler, `invite:${zeile.tenant_contractor_id}`)
            return (
            <tr key={zeile.tenant_contractor_id}>
              <th scope="row">{zeile.display_name}</th>
              <td className="mono">{zeile.short_code}</td>
              <td>
                <label className="nur-vorlesen" htmlFor={`tm-hl-${zeile.tenant_contractor_id}`}>
                  Name in FinSuites für {zeile.display_name}
                </label>
                <input
                  id={`tm-hl-${zeile.tenant_contractor_id}`}
                  type="text"
                  placeholder="Vor- und Nachname; weitere mit Komma"
                  value={hlNamen[zeile.tenant_contractor_id] ?? zeile.legacy_name ?? ''}
                  onChange={(ereignis) => {
                    setzeHlNamen({ ...hlNamen, [zeile.tenant_contractor_id]: ereignis.target.value })
                  }}
                />
                {' '}
                <Knopf
                  variante="still"
                  // Erst nach einer Änderung: ein Knopf, der dasselbe noch einmal schreibt, ist Rauschen.
                  disabled={hlName.isPending || hlNamen[zeile.tenant_contractor_id] === undefined}
                  onClick={() => {
                    const wert = (hlNamen[zeile.tenant_contractor_id] ?? '').trim()
                    hlName.mutate({ tcId: zeile.tenant_contractor_id, name: wert === '' ? null : wert })
                  }}
                >
                  Speichern
                </Knopf>
              </td>
              <td className="mono">{zeile.invoice_prefix}</td>
              <td className="mono">{zeile.kreditor_no ?? '—'}</td>
              <td>
                {STEUERPROFIL_TEXT[zeile.vat_mode]}
                {zeile.verified_at === null ? ' — ungeprüft' : ` — geprüft am ${datumZeit(zeile.verified_at)}`}
                {istPlattformAdmin && zeile.verified_at === null && (
                  <>
                    {' '}
                    {/* Der Knopf öffnet die Nachfrage, er löst nichts aus: die Freigabe gilt
                        plattformweit und lässt sich nirgends zurücknehmen. */}
                    <Knopf
                      variante="still"
                      disabled={pruefen.isPending}
                      onClick={() => { pruefen.reset(); setzeFreigabe(zeile) }}
                    >
                      Steuerprofil prüfen
                    </Knopf>
                  </>
                )}
              </td>
              <td>
                {/* Drei Zustaende, drei Saetze: hat einen Zugang - ist eingeladen, wartet - noch nichts.
                    Vorher stand bei einer eingeladenen Person "kein Login" neben einem leeren Feld, und
                    die Adresse, an die die Einladung ging, war nirgends zu sehen. */}
                {zeile.owner_email !== null ? (
                  <>Angemeldet als <span className="mono">{zeile.owner_email}</span></>
                ) : zeile.invited_email !== null ? (
                  <>
                    Eingeladen an <span className="mono">{zeile.invited_email}</span>
                    {zeile.invited_at === null ? '' : ` am ${datumZeit(zeile.invited_at)}`}
                    {' '}
                    <Knopf
                      variante="still"
                      disabled={einladen.isPending}
                      onClick={() => {
                        einladen.mutate({ tcId: zeile.tenant_contractor_id, email: zeile.invited_email ?? '' })
                      }}
                    >
                      Einladung erneut senden
                    </Knopf>
                  </>
                ) : (
                  <>
                <label className="nur-vorlesen" htmlFor={`tm-invite-${zeile.tenant_contractor_id}`}>
                  E-Mail für die Einladung
                </label>
                <input
                  id={`tm-invite-${zeile.tenant_contractor_id}`}
                  type="email"
                  placeholder="E-Mail-Adresse"
                  value={einladungen[zeile.tenant_contractor_id] ?? ''}
                  aria-invalid={einladungsFehler === undefined ? undefined : true}
                  aria-describedby={
                    einladungsFehler === undefined
                      ? undefined
                      : `tm-invite-${zeile.tenant_contractor_id}-fehler`
                  }
                  onChange={(ereignis) => {
                    setzeEinladungen({
                      ...einladungen, [zeile.tenant_contractor_id]: ereignis.target.value,
                    })
                  }}
                />
                {' '}
                <Knopf
                  variante="still"
                  disabled={einladen.isPending}
                  onClick={() => {
                    const geprueft = inviteTeamMemberSchema.safeParse({
                      email: einladungen[zeile.tenant_contractor_id] ?? '',
                    })
                    if (!geprueft.success) {
                      setzePruefFehler({
                        feld: `invite:${zeile.tenant_contractor_id}`,
                        meldung: ersterFehler(geprueft.error).meldung,
                      })
                      return
                    }
                    setzePruefFehler(null)
                    einladen.mutate({ tcId: zeile.tenant_contractor_id, email: geprueft.data.email })
                  }}
                >
                  Einladen
                </Knopf>
                {einladungsFehler === undefined ? null : (
                  <span
                    id={`tm-invite-${zeile.tenant_contractor_id}-fehler`}
                    role="alert"
                    className="feld-fehler"
                  >
                    {einladungsFehler}
                  </span>
                )}
                  </>
                )}
              </td>
              <td>
                <Knopf variante="still" onClick={() => { entferne.reset(); setzeEntfernen(zeile) }}>
                  {zeile.display_name} entfernen
                </Knopf>
              </td>
            </tr>
            )
          })
        )}
      </Tabelle>

      <h2>Neuer Auftragnehmer</h2>
      <Leiste beschriftung="Neuen Auftragnehmer anlegen">
        <Feld label="Name" htmlFor="tm-neu-name" fehler={meldungFuer(pruefFehler, 'display_name')}>
          <input
            id="tm-neu-name"
            type="text"
            value={neuerName}
            onChange={(ereignis) => { setzeNeuenNamen(ereignis.target.value) }}
          />
        </Feld>
        <Feld label="E-Mail-Adresse" htmlFor="tm-neu-email" fehler={meldungFuer(pruefFehler, 'email')}>
          <input
            id="tm-neu-email"
            type="email"
            value={neueEmail}
            onChange={(ereignis) => { setzeNeueEmail(ereignis.target.value) }}
          />
        </Feld>
        <Knopf
          disabled={anlegen.isPending || einladen.isPending}
          onClick={() => {
            const geprueft = createTeamMemberSchema.safeParse({ display_name: neuerName })
            if (!geprueft.success) {
              setzePruefFehler(ersterFehler(geprueft.error))
              return
            }
            const email = neueEmail.trim()
            if (email !== '') {
              const geprueftEmail = inviteTeamMemberSchema.safeParse({ email })
              if (!geprueftEmail.success) {
                setzePruefFehler({ feld: 'email', meldung: ersterFehler(geprueftEmail.error).meldung })
                return
              }
            }
            setzePruefFehler(null)
            anlegen.mutate({ ...geprueft.data, email })
          }}
        >
          {neueEmail.trim() === '' ? 'Auftragnehmer anlegen' : 'Anlegen und einladen'}
        </Knopf>
      </Leiste>

      {meldung !== null && <p className="meldung">{meldung}</p>}
      {/* Eine Meldung mit Feld steht bei ihrem Feld; hier bleibt nur, was keinem Feld gilt. */}
      {pruefFehler !== null && pruefFehler.feld === null && (
        <p role="alert" className="fehler">{pruefFehler.meldung}</p>
      )}
      {aktionsFehler !== null && <p role="alert" className="fehler">{fehlermeldung(aktionsFehler)}</p>}

      {inaktive === 0 ? null : (
        <p>
          <label>
            <input type="checkbox" checked={inaktiveZeigen} onChange={(e) => { setzeInaktiveZeigen(e.target.checked) }} />
            {' '}{inaktive === 1 ? 'Eine stillgelegte Person anzeigen' : `${inaktive} stillgelegte Personen anzeigen`}
          </label>
        </p>
      )}

      {entfernen === null ? null : (
        <Dialog titel={`${entfernen.display_name} entfernen`} offen onSchliessen={() => { setzeEntfernen(null) }}>
          <p className="erklaerung">
            Hängen an dieser Person schon Provisionen, Abschlüsse oder Rechnungen, wird sie stillgelegt und bleibt
            im Archiv — Buchhaltung löscht nichts, was einmal Geld war. Sonst verschwindet sie samt offener Einladung.
          </p>
          {entferne.isError ? <p role="alert" className="fehler">{fehlermeldung(entferne.error)}</p> : null}
          <p className="knoepfe">
            <Knopf variante="warnung" disabled={entferne.isPending} onClick={() => { entferne.mutate(entfernen.tenant_contractor_id) }}>
              Entfernen
            </Knopf>
            <Knopf variante="still" erstfokus onClick={() => { setzeEntfernen(null) }}>Abbrechen</Knopf>
          </p>
        </Dialog>
      )}

      {freigabe === null ? null : (
        <SteuerprofilFreigabeDialog
          name={freigabe.display_name}
          profil={STEUERPROFIL_TEXT[freigabe.vat_mode]}
          laeuft={pruefen.isPending}
          fehler={pruefen.error === null ? null : fehlermeldung(pruefen.error)}
          onBestaetigen={() => { pruefen.mutate(freigabe.tenant_contractor_id) }}
          onSchliessen={() => { pruefen.reset(); setzeFreigabe(null) }}
        />
      )}
    </section>
  )
}

// -----------------------------------------------------------------------------------------------------
// Webhook
// -----------------------------------------------------------------------------------------------------

function FeldTabelle({ titel, felder }: { titel: string; felder: readonly HlFeld[] }): ReactElement {
  return (
    <Tabelle
      beschriftung={titel}
      kopf={(
        <tr>
          <th scope="col">Schlüssel im Webhook</th>
          <th scope="col">Pflicht</th>
          <th scope="col">Bedeutung</th>
        </tr>
      )}
    >
      {felder.map((feld) => (
        <tr key={feld.key}>
          <th scope="row" className="mono">{feld.key}</th>
          <td>{feld.pflicht ? 'ja' : 'nein'}</td>
          <td>{feld.ziel}</td>
        </tr>
      ))}
    </Tabelle>
  )
}

function WebhookReiter({ tenantId }: { tenantId: string }): ReactElement {
  const tid = encodeURIComponent(tenantId)
  const [neuesSecret, setzeNeuesSecret] = useState<WebhookGeheimnis | null>(null)
  // Das Erneuern ist unumkehrbar und trifft sofort: die alten URLs sind tot, FinSuites liefert bis zum
  // Eintragen der neuen nichts mehr. Wie jede unumkehrbare Handlung dieser Anwendung (Freigabe,
  // Ablehnung, Auszahlung, Storno) fragt sie deshalb erst im Dialog nach.
  const [fragtNach, setzeFragtNach] = useState(false)

  const einstellungen = useQuery({
    queryKey: ['einstellungen', tenantId],
    queryFn: () => api.get<{ tenant: TenantEinstellungen }>(`/api/t/${tid}/einstellungen`),
  })
  const erneuern = useMutation({
    mutationFn: () => api.post<WebhookGeheimnis>(`/api/t/${tid}/einstellungen/webhook-secret/rotate`, {}),
    onSuccess: (antwort) => {
      setzeNeuesSecret(antwort)
      setzeFragtNach(false)
    },
  })

  const slug = einstellungen.data?.tenant.slug ?? ':slug'
  const queryClient = useQueryClient()
  const [externTags, setzeExternTags] = useState<string | null>(null)
  const externTagsSpeichern = useMutation({
    mutationFn: (wert: string) => api.patch<{ tenant: TenantEinstellungen }>(`/api/t/${tid}/einstellungen`, { extern_tags: wert }),
    onSuccess: () => { setzeExternTags(null); void queryClient.invalidateQueries({ queryKey: ['einstellungen', tenantId] }) },
  })
  const hostFeld = useMutation({
    mutationFn: (wert: 'host' | 'sonstige') =>
      api.patch<{ tenant: TenantEinstellungen }>(`/api/t/${tid}/einstellungen`, { host_feld: wert }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['einstellungen', tenantId] }) },
  })

  return (
    <section aria-label="Webhook">
      {einstellungen.data === undefined ? null : (
        <Feld
          label="Das Feld host gehört zu"
          htmlFor="wh-host-feld"
          hinweis="Gilt für neue Lieferungen und für „Fehlendes aus FinSuites ergänzen“. Ein ausdrücklich geschicktes Feld sonstige geht immer vor."
        >
          <select
            id="wh-host-feld"
            value={einstellungen.data.tenant.host_feld}
            disabled={hostFeld.isPending}
            onChange={(e) => { hostFeld.mutate(e.target.value === 'sonstige' ? 'sonstige' : 'host') }}
          >
            <option value="host">Empfehlungsgeber</option>
            <option value="sonstige">Sonstige Rolle (z. B. Host)</option>
          </select>
        </Feld>
      )}
      {hostFeld.isError ? <p role="alert" className="fehler">{fehlermeldung(hostFeld.error)}</p> : null}
      {einstellungen.data === undefined ? null : (
        <Leiste beschriftung="Tags für externe Leads">
          <Feld
            label="FinSuites-Tags für externe Leads"
            htmlFor="wh-extern-tags"
            hinweis="Ohne Tag gilt ein Lead als intern, von der Firma gestellt. Trägt der Kontakt einen dieser Tags (kommagetrennt), wird der Abschluss extern — etwa bei Empfehlung oder Cold Call."
          >
            <input
              id="wh-extern-tags"
              type="text"
              value={externTags ?? einstellungen.data.tenant.extern_tags}
              onChange={(e) => { setzeExternTags(e.target.value) }}
            />
          </Feld>
          <Knopf
            variante="still"
            disabled={externTagsSpeichern.isPending || externTags === null}
            onClick={() => { externTagsSpeichern.mutate(externTags ?? '') }}
          >
            Speichern
          </Knopf>
        </Leiste>
      )}
      {externTagsSpeichern.isError ? <p role="alert" className="fehler">{fehlermeldung(externTagsSpeichern.error)}</p> : null}
      <p className="hinweis">
        FinSuites schickt zwei Ströme an diese Anwendung. Die URLs enthalten das Secret und lauten
        <span className="mono"> /hooks/hl/{slug}/deal/&lt;secret&gt;</span> und
        <span className="mono"> /hooks/hl/{slug}/payment/&lt;secret&gt;</span>.
      </p>
      <p className="hinweis">
        Legen Sie im FinSuites-Workflow eine Webhook-Aktion an und tragen Sie genau diese Schlüssel ein.
        Unbekannte Schlüssel werden ignoriert, ein fehlendes Pflichtfeld landet als „nicht lesbar“ in der
        Prüfen-Liste.
      </p>

      <FeldTabelle titel="Abschluss-Strom (deal)" felder={DEAL_FELDER} />
      <FeldTabelle titel="Zahlungs-Strom (payment)" felder={PAYMENT_FELDER} />

      {/* Der Hinweis steht VOR dem Knopf: unter ihm wäre er erst nach dem Klick gelesen. */}
      <p className="hinweis">
        Beim Erneuern werden die bisherigen URLs sofort ungültig. Tragen Sie die neuen URLs unmittelbar in
        beide Workflows ein.
      </p>
      <p className="knoepfe">
        <Knopf
          variante="warnung"
          disabled={erneuern.isPending}
          onClick={() => { setzeFragtNach(true) }}
        >
          Secret erneuern
        </Knopf>
      </p>

      {fragtNach && (
        <Dialog titel="Webhook-Secret erneuern" offen onSchliessen={() => { setzeFragtNach(false) }}>
          <p className="erklaerung">
            Die beiden bisherigen Hook-URLs werden sofort ungültig. Bis Sie die neuen URLs in beide
            FinSuites-Workflows eingetragen haben, kommt kein Abschluss und keine Zahlung mehr an.
          </p>
          <p className="erklaerung">
            Das neue Secret wird genau einmal angezeigt und danach nie wieder.
          </p>
          {erneuern.isError && <p role="alert" className="fehler">{fehlermeldung(erneuern.error)}</p>}
          {/* Der Anfangsfokus liegt auf „Abbrechen“: eine gehaltene oder zweimal getippte
              Eingabetaste auf „Secret erneuern“ darf nicht beide Hook-URLs ungültig machen. */}
          <p className="knoepfe">
            <Knopf variante="warnung" disabled={erneuern.isPending} onClick={() => { erneuern.mutate() }}>
              Jetzt erneuern
            </Knopf>
            <Knopf variante="still" erstfokus onClick={() => { setzeFragtNach(false) }}>Abbrechen</Knopf>
          </p>
        </Dialog>
      )}

      {neuesSecret !== null && (
        <div className="detail" role="group" aria-label="Neues Webhook-Secret">
          <p className="warnung">Dieses Secret wird nie wieder angezeigt. Bitte jetzt übernehmen.</p>
          <p className="mono">{neuesSecret.webhook_secret}</p>
          <p className="mono">{neuesSecret.hook_urls.deal}</p>
          <p className="mono">{neuesSecret.hook_urls.payment}</p>
          <Knopf variante="still" onClick={() => { setzeNeuesSecret(null) }}>Secret verbergen</Knopf>
        </div>
      )}
      {/* Steht der Dialog offen, steht die Meldung schon in ihm — sonst zweimal dasselbe. */}
      {erneuern.isError && !fragtNach && (
        <p role="alert" className="fehler">{fehlermeldung(erneuern.error)}</p>
      )}
      {einstellungen.isError && <p role="alert" className="fehler">{fehlermeldung(einstellungen.error)}</p>}
    </section>
  )
}

// -----------------------------------------------------------------------------------------------------
// Audit-Log
// -----------------------------------------------------------------------------------------------------

const AUDIT_SPALTEN = 6

function AuditReiter({ tenantId, suche, setzeSuche }: {
  tenantId: string
  suche: EinstellungenSuche
  setzeSuche: (suche: EinstellungenSuche) => void
}): ReactElement {
  const tid = encodeURIComponent(tenantId)
  const [entwurf, setzeEntwurf] = useState({
    entity: suche.entity ?? '', actor: suche.actor ?? '', from: suche.from ?? '', to: suche.to ?? '',
  })

  // Gefiltert wird auf dem Server: das Audit-Log wächst und wird nie vollständig in den Browser geholt.
  const audit = useQuery({
    queryKey: ['audit', tenantId, suche.entity, suche.actor, suche.from, suche.to],
    queryFn: () => api.get<{ eintraege: AuditZeile[] }>(`/api/t/${tid}/audit`, {
      entity: suche.entity, actor: suche.actor, from: suche.from, to: suche.to,
    }),
  })

  const eintraege = audit.data?.eintraege ?? []

  return (
    <section aria-label="Audit-Log">
      <Leiste beschriftung="Audit-Log filtern">
        <Feld label="Objekt (Typ oder Typ:ID)" htmlFor="au-entity">
          <input
            id="au-entity"
            type="text"
            value={entwurf.entity}
            onChange={(ereignis) => { setzeEntwurf({ ...entwurf, entity: ereignis.target.value }) }}
          />
        </Feld>
        <Feld label="Akteur (Benutzer-ID)" htmlFor="au-actor">
          <input
            id="au-actor"
            type="text"
            value={entwurf.actor}
            onChange={(ereignis) => { setzeEntwurf({ ...entwurf, actor: ereignis.target.value }) }}
          />
        </Feld>
        <Feld label="Von" htmlFor="au-von">
          <input
            id="au-von"
            className="mono"
            type="date"
            value={entwurf.from}
            onChange={(ereignis) => { setzeEntwurf({ ...entwurf, from: ereignis.target.value }) }}
          />
        </Feld>
        <Feld label="Bis" htmlFor="au-bis">
          <input
            id="au-bis"
            className="mono"
            type="date"
            value={entwurf.to}
            onChange={(ereignis) => { setzeEntwurf({ ...entwurf, to: ereignis.target.value }) }}
          />
        </Feld>
        <Knopf
          onClick={() => {
            setzeSuche({
              ...suche,
              entity: entwurf.entity === '' ? undefined : entwurf.entity,
              actor: entwurf.actor === '' ? undefined : entwurf.actor,
              from: entwurf.from === '' ? undefined : entwurf.from,
              to: entwurf.to === '' ? undefined : entwurf.to,
            })
          }}
        >
          Filtern
        </Knopf>
      </Leiste>

      <Tabelle
        beschriftung="Audit-Log dieses Auftraggebers"
        kopf={(
          <tr>
            <th scope="col">Zeitpunkt</th>
            <th scope="col">Aktion</th>
            <th scope="col">Objekt</th>
            <th scope="col">Akteur</th>
            <th scope="col">Grund</th>
            <th scope="col">Request</th>
          </tr>
        )}
      >
        {audit.isPending ? (
          <TabellenZustand art="laedt" beschriftung="Das Protokoll wird geladen." spalten={AUDIT_SPALTEN} />
        ) : audit.isError ? (
          <TabellenZustand
            art="fehler"
            fehler={audit.error}
            onWiederholen={() => { void audit.refetch() }}
            spalten={AUDIT_SPALTEN}
          />
        ) : eintraege.length === 0 ? (
          <TabellenZustand
            art="leer"
            titel="Keine Einträge für diese Auswahl"
            hinweis="Das Protokoll zeigt jede Änderung. Erweitern Sie den Zeitraum oder entfernen Sie den Filter."
            spalten={AUDIT_SPALTEN}
          />
        ) : (
          eintraege.map((eintrag) => (
            <tr key={eintrag.id}>
              <th scope="row" className="mono">{datumZeit(eintrag.created_at)}</th>
              <td>{eintrag.action}</td>
              <td className="mono">{eintrag.entity_type}:{eintrag.entity_id}</td>
              <td className="mono">{eintrag.actor_user_id ?? eintrag.actor_type}</td>
              <td>{eintrag.reason ?? '—'}</td>
              <td className="mono">{eintrag.request_id ?? '—'}</td>
            </tr>
          ))
        )}
      </Tabelle>
    </section>
  )
}

// -----------------------------------------------------------------------------------------------------
// Verfahrensdokumentation
// -----------------------------------------------------------------------------------------------------

function DokumentationReiter(): ReactElement {
  return (
    <section aria-label="Verfahrensdokumentation">
      <p className="hinweis">
        Die Verfahrensdokumentation beschreibt, welches System welchen Datensatz führt, wie ein Beleg
        entsteht, wie er zehn Jahre aufbewahrt wird und wie eine Wiederherstellung abläuft. Sie gehört zu
        den Unterlagen, die einer Betriebsprüfung vorgelegt werden.
      </p>
      <p>
        <a href="/verfahrensdokumentation.md">Verfahrensdokumentation öffnen</a>
      </p>
    </section>
  )
}
